import { useCallback, useEffect, useState } from "react";
import {
  getHostEarnings,
  getWallet,
  getWalletTransactions,
} from "@services/krent.service";
import { PaymentMethod } from "@interfaces/wallet.interface";

export type HostPayoutStatus =
  | "unpaid"
  | "paid"
  | "paid_outside_system"
  | "cancelled";

export interface HostEarning {
  id: string;
  referenceId: string;
  bookingReference: string | null;
  propertyTitle: string | null;
  coverImage: string | null;
  productType: string | null;
  status: string;
  arrivalDate: string;
  departureDate: string;
  bookingPrice: number;
  commissionPercent: number | null;
  hostPayout: {
    status: HostPayoutStatus;
    amount: number;
    amountPaid?: number;
    reference?: string;
    paidAt?: string;
    reversedAt?: string;
  };
}

export interface WalletEntry {
  id: string;
  amount: number;
  type: "Payment" | "Withdrawal" | "Payout" | "CautionFee" | "Adjustment";
  status: "Requested" | "Rejected" | "Paid" | "Disputed";
  destination: "Wallet" | "Account";
  description?: string;
  external_ref?: string;
  otherDetails?: string;
  createdAt: string;
}

const HISTORY_PAGE_SIZE = 10;

export default function useHostWallet(walletId?: string) {
  const [wallet, setWallet] = useState<PaymentMethod | null>(null);
  const [walletError, setWalletError] = useState("");
  const [loadingWallet, setLoadingWallet] = useState(true);

  const [earnings, setEarnings] = useState<HostEarning[]>([]);
  const [totals, setTotals] = useState({ earned: 0, paidOut: 0, owed: 0, cautionFeesHeld: 0 });
  const [earningsCount, setEarningsCount] = useState(0);
  const [earningsPage, setEarningsPage] = useState(1);
  const [payoutStatus, setPayoutStatus] = useState("");
  const [loadingEarnings, setLoadingEarnings] = useState(true);

  const [history, setHistory] = useState<WalletEntry[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((key) => key + 1), []);

  useEffect(() => {
    if (!walletId) {
      setLoadingWallet(false);
      return;
    }
    const controller = new AbortController();
    setLoadingWallet(true);
    getWallet(walletId, controller.signal)
      .then((res: any) => {
        if (controller.signal.aborted) return;
        if (!res?.success) throw new Error(res?.errors?.[0]?.message || "Unable to load your wallet.");
        setWallet(res.data);
        setWalletError("");
      })
      .catch((err) => {
        if (!controller.signal.aborted) setWalletError(err.message || "Unable to load your wallet.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingWallet(false);
      });
    return () => controller.abort();
  }, [walletId, refreshKey]);

  useEffect(() => {
    const controller = new AbortController();
    setLoadingEarnings(true);
    getHostEarnings(earningsPage, payoutStatus, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        setEarnings(res.data);
        setTotals(res.totals);
        setEarningsCount(res.total);
      })
      .catch(() => {
        if (!controller.signal.aborted) setEarnings([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingEarnings(false);
      });
    return () => controller.abort();
  }, [earningsPage, payoutStatus, refreshKey]);

  useEffect(() => {
    if (!walletId) {
      setLoadingHistory(false);
      return;
    }
    const controller = new AbortController();
    setLoadingHistory(true);
    getWalletTransactions(
      walletId,
      controller.signal,
      `?sort=-createdAt&page=${historyPage}&limit=${HISTORY_PAGE_SIZE}`,
    )
      .then((res: any) => {
        if (!controller.signal.aborted) setHistory(res?.data || []);
      })
      .catch(() => {
        if (!controller.signal.aborted) setHistory([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingHistory(false);
      });
    return () => controller.abort();
  }, [walletId, historyPage, refreshKey]);

  const filterByPayoutStatus = useCallback((status: string) => {
    setPayoutStatus(status);
    setEarningsPage(1);
  }, []);

  return {
    wallet,
    walletError,
    loadingWallet,
    earnings,
    totals,
    earningsCount,
    earningsPage,
    setEarningsPage,
    payoutStatus,
    filterByPayoutStatus,
    loadingEarnings,
    history,
    historyPage,
    setHistoryPage,
    hasMoreHistory: history.length === HISTORY_PAGE_SIZE,
    loadingHistory,
    refresh,
  };
}
